import React from 'react';
import ApolloClient from 'apollo-boost';
import { ApolloProvider } from '@apollo/react-hooks';
import Layout from '../components/layout';
import SEO from '../components/seo';
import { SettingProvider } from '../lib/context/settings';
import { AdsProvider } from '../lib/context/ads';
import { Button, Col, Row } from 'antd';
import { device } from '../lib/theme';
import styled from 'styled-components';
import { SecondaryTitle, Title } from '../components/styles';
import { TravelCDCBanner } from '../styles/travel';

const client = new ApolloClient<any>({
  uri: `https://${process.env.GATSBY_WORDPRESS_URL_PATH}/graphql`,
  fetchOptions: {
    credentials: 'include',
  },
});

const HeroContainer = styled(Row)`
  background-image: url('https://aaacdndev.blob.core.windows.net/wordpress/2021/05/Screenshot-2021-05-19-143112.jpg');
  background-size: cover;
  background-position: right;
  background-repeat: no-repeat;
  height: 340px;

  h1 {
    max-width: 80%;
    font-size: 32px;
    line-height: 1.1;
  }

  @media ${device.laptop} {
    height: 560px;
    h1 {
      max-width: 60%;
      font-size: 56px;
      line-height: 1.2;
    }

    background-position: center center;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-top: 32px;
  margin-bottom: 64px;

  .ant-btn {
    min-width: 250px;
    min-height: 50px;
    margin-bottom: 16px;
    margin-left: 0px;
    margin-right: 8px;
  }

  .withSubText {
    display: flex;
    flex-flow: column nowrap;
  }

  button.ant-btn > .subtext {
    font-size: 10px;
    font-weight: 700;
    color: #cd1227;
    line-height: 1;
  }

  @media ${device.tablet} {
    flex-flow: row nowrap;
    button {
      margin-bottom: 0;
    }
  }
`;

const PlanBlockContainer = styled(Col)`
  text-align: center;
  color: black;
  background-color: white;
  box-shadow: 0px 3px 6px #00000029;
  margin-top: 70px;
  padding: 0px 16px 8px;

  img {
    position: absolute;
    left: 41%;
    top: -45px;
  }

  @media ${device.laptop} {
    margin-top: 0px;

    img {
      position: absolute;
      left: 42%;
      top: -45px;
    }
  }
`;

const IframeCol = styled(Col)`
  iframe {
    width: 100%;
  }
`;

const AlbemarleLandingPage: React.FC<any> = () => {
  return (
    <ApolloProvider client={client as any}>
      <SettingProvider>
        <AdsProvider>
          <Layout key={`${typeof window}`}>
            <SEO
              title="Travel Insurance | AAA Carolinas"
              image=""
              canonicalUrl="/travel-insurance"
            />
            <HeroContainer align="bottom">
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <Title
                  white
                  style={{
                    padding: '5px',
                    textAlign: 'left',
                  }}
                >
                  Travel Insurance
                </Title>
                <h2
                  style={{
                    color: 'white',
                    textAlign: 'left',
                    marginTop: '0px',
                  }}
                >
                  4 Reasons Why Travel Insurance is a Must
                </h2>
              </Col>
            </HeroContainer>
            <TravelCDCBanner>
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                {`Effective January 26, the CDC is expanding its travel
                requirements. All air passengers entering the United States will
                be required to have a negative COVID-19 test. It must be a viral
                test taken within the 3 days before the passenger’s flight to
                the U.S., and passengers must provide written documentation of
                their laboratory test result to the airline or provide
                documentation of having recovered from COVID-19.`}
                <Button
                  type="text"
                  href="https://www.allianztravelinsurance.com/coverage-alerts/2019-novel-coronavirus.htm"
                >
                  Learn More{' '}
                  <img
                    src="/img/icon/right-arrow-white.png"
                    style={{ height: 12, marginLeft: 12 }}
                  />
                </Button>
              </Col>
            </TravelCDCBanner>
            <Row style={{ paddingTop: 48, paddingBottom: 48 }}>
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <Row>
                  <Col xs={{ span: 24 }}>
                    <h2>
                      <strong>
                        <span style={{ color: '#ed1b2e' }}>1.</span> To Protect
                        Your Vacation Investment
                      </strong>
                    </h2>
                    <p>
                      If something happens, like a loved one is hospitalized
                      right before your trip, with trip cancellation benefits,
                      you can be reimbursed for prepaid, nonrefundable trip
                      costs when you cancel for a covered reason.
                    </p>
                    <h2>
                      <strong>
                        <span style={{ color: '#ed1b2e' }}>2.</span> Medical
                        Emergencies Abroad Are Expensive
                      </strong>
                    </h2>
                    <p>
                      Most foreign countries do not accept US insurance plans.
                      Some are even requiring proof of travel insurance with
                      medical benefits before they let you into the country! You
                      don’t want to get caught paying out-of-pocket for your
                      medical treatment if you’re in an accident or get sick
                      while traveling out of the country.
                    </p>
                    <h2>
                      <strong>
                        <span style={{ color: '#ed1b2e' }}>3.</span> Don’t Let
                        Minor Mishaps Ruin Your Trip
                      </strong>
                    </h2>
                    <p>
                      Missed flights or missing luggage aren’t a great way to
                      start a vacation—but travel and baggage delay benefits can
                      help ease the pain. Get reimbursed for unexpected
                      expenses, like essential item purchases if your luggage is
                      missing or overnight accommodations due to a covered
                      departure delay.
                    </p>
                    <h2>
                      <strong>
                        <span style={{ color: '#ed1b2e' }}>4.</span> Travel
                        Insurance Is More Affordable Than You Think.
                      </strong>
                    </h2>
                    <p>
                      Your AAA Travel Agent will help you find the best option
                      for your needs and budget.
                      <br /> With a AAA-exclusive TripAssist plan from our
                      trusted travel insurance partner Allianz Global
                      Assistance, you can relax before and during your trips
                      knowing you can be protected in case of covered:
                    </p>
                    <ul>
                      <li>Unexpected cancellations</li>
                      <li>Travel delays</li>
                      <li>Medical emergencies</li>
                      <li>Lost luggage</li>
                      <li>And more</li>
                    </ul>
                  </Col>
                </Row>
                <Row>
                  <Col xs={{ span: 24 }}>
                    <h2>
                      <strong>
                        Call your local AAA travel agent today:{' '}
                        <a
                          href="tel:1-800-444-8691"
                          style={{ color: '#004F96' }}
                        >
                          1-800-444-8691
                        </a>
                      </strong>
                    </h2>
                    <Buttons>
                      <Button
                        type="primary"
                        href="https://www.agentmaxonline.com/agentmaxweb/storefront/index.html#/home/sf?accamnum=C209960&referrer=createlink"
                      >
                        Get a Quote
                      </Button>
                      <Button
                        type="primary"
                        href="https://locator.carolinas.aaa.com/"
                      >
                        Locate your nearest AAA
                      </Button>
                    </Buttons>
                  </Col>
                </Row>

                <Row>
                  <Col xs={{ span: 24 }}>
                    <SecondaryTitle style={{ textAlign: 'center' }}>
                      <strong>Unique TripAssist Plan Benefits</strong>
                    </SecondaryTitle>
                    <p style={{ textAlign: 'center' }}>
                      Purchasing a TripAssist plan through AAA comes with some
                      value-adding benefits that aren’t available with all other
                      Allianz Global Assistance travel protection plans,
                      including:
                    </p>
                    <Row style={{ marginTop: 64, marginBottom: 32 }}>
                      <PlanBlockContainer xs={{ span: 24 }} md={{ span: 7 }}>
                        <img src="https://aaacdndev.blob.core.windows.net/wordpress/2020/12/car-icon.png" />{' '}
                        <br />
                        <h2>
                          <strong>Vehicle return</strong>
                        </h2>
                        <p>
                          to reimburse you the cost of transporting your vehicle
                          back to your residence when a covered illness or
                          injury prevents completion of your trip.
                        </p>
                      </PlanBlockContainer>
                      <PlanBlockContainer
                        xs={{ span: 24 }}
                        md={{ span: 7, offset: 1 }}
                      >
                        <img src="https://aaacdndev.blob.core.windows.net/wordpress/2020/12/ship__icon.png" />{' '}
                        <br />
                        <h2>
                          <strong>Missed port of call</strong>
                        </h2>
                        <p>
                          that can provide you with $100 payments, up to your
                          plan’s maximum amount, when your cruise misses a
                          scheduled port of call on your original itinerary or
                          replaces it with another port of call.
                        </p>
                      </PlanBlockContainer>
                      <PlanBlockContainer
                        xs={{ span: 24 }}
                        md={{ span: 7, offset: 1 }}
                      >
                        <img src="https://aaacdndev.blob.core.windows.net/wordpress/2020/12/shield-icon.png" />{' '}
                        <br />
                        <h2>
                          <strong>
                            SmartBenefits<sup>SM</sup>
                          </strong>
                        </h2>
                        <p>
                          that can provide automatic, proactive payments for
                          covered delays on monitored flights and a
                          no-receipts-required payment option for lost or
                          delayed baggage*.
                        </p>
                      </PlanBlockContainer>
                    </Row>
                  </Col>
                </Row>
                <Row>
                  <Col xs={{ span: 24 }} md={{ span: 11 }}>
                    <h2>
                      <strong>
                        How TripAssist Compares to Cruise, Credit Card, and Tour
                        Insurance.{' '}
                      </strong>
                    </h2>
                    <p>
                      While it can be convenient to purchase a travel
                      supplier-offered insurance plan or rely on credit card
                      protections when booking your trip, be sure to compare the
                      benefits because third-party plans like TripAssist usually
                      have more comprehensive, secure, and flexible benefits.
                    </p>
                    <p style={{ color: '#004F96' }}>
                      <strong>
                        Learn more about some key differences between supplier
                        and third-party travel insurance plans.
                      </strong>
                    </p>
                  </Col>
                  <IframeCol xs={{ span: 24 }} md={{ span: 10, offset: 2 }}>
                    <iframe
                      src="https://player.vimeo.com/video/491289115"
                      width="520"
                      height="275"
                      frameBorder="0"
                      allow="autoplay; fullscreen"
                    ></iframe>
                  </IframeCol>
                </Row>
                <Row style={{ marginTop: 32 }}>
                  <Col xs={{ span: 24 }}>
                    <p>
                      <small>
                        *When you opt in and provide flight information, we’ll
                        monitor flights and send flight status and benefit
                        alerts, including alerts about flight delays that
                        qualify for automated travel delay payment. Standard
                        message/data rates apply to SMS alerts. Automated claims
                        and payment system availability is not guaranteed and is
                        subject to our sole discretion. No receipts are required
                        for non-monitored flights to qualify for a $100 payment
                        per day–just proof of the covered delay. No receipts for
                        expenses are required to qualify for a $100 baggage
                        delay payment—just proof of baggage delay. All claims
                        subject to policy terms, conditions, and exclusions.
                      </small>
                    </p>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Layout>
        </AdsProvider>
      </SettingProvider>
    </ApolloProvider>
  );
};

export default AlbemarleLandingPage;
